import { AANAccountTypeToAccountType, AccountTypes } from '@interfaces/models';
import { JOINT_ACCOUNTS_TYPES } from '@interfaces/state';
import { createSelector } from '@ngrx/store';

import {
  DecedentInfoState,
  getDecedentDOD,
  getDecedentFirstName,
  getVerifyDecedentPayload,
} from '../../reducers/decedent-info/decedent-info.reducer';
import * as fromFeature from '../../reducers/index';
import { selectInheritanceAdvisedClientWarningsToggle } from '../feature/feature.selectors';
import {
  selectClientDateOfBirth,
  selectLast4IdOnFile,
} from '../personal-data/personal-data.selectors';
import { joinWithSpace } from '../receipt/cashier-nonretirement';
import { selectStateTransactions } from '../transaction-state/transaction-state.selectors';

export const selectDecedentInfoState = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state,
);
export const selectDecedentFirstName = createSelector(
  fromFeature.getDecedentInfoState,
  getDecedentFirstName,
);
export const selectDecedentDOD = createSelector(fromFeature.getDecedentInfoState, getDecedentDOD);
export const selectDecedentFullName = createSelector(fromFeature.getDecedentInfoState, (state) =>
  joinWithSpace([state.firstName, state.middleName, state.lastName]),
);
export const selectDecedentInheritanceWaiverState = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state?.inheritanceWaiverState || '',
);
export const selectVerifyDecedentPayload = createSelector(
  fromFeature.getDecedentInfoState,
  getVerifyDecedentPayload,
);
export const selectIsDeathVerified = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state.isDeathVerified,
);
export const selectIsReviewModalVisible = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state.isReviewModalVisible,
);
export const selectDecedentIdExceededAttempts = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state.decedentIdExceededAttempts,
);

export const selectDecedentCity = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state.city,
);

export const selectDecedentState = createSelector(
  fromFeature.getDecedentInfoState,
  (state) => state.state,
);

export const selectBeneMatch = createSelector(
  selectDecedentInfoState,
  selectLast4IdOnFile,
  selectClientDateOfBirth,
  (decedentState: DecedentInfoState, last4OnFile: string, clientDataOfBirth: string) => {
    if (
      decedentState &&
      decedentState.decedentId_private &&
      decedentState.dateOfBirth &&
      last4OnFile &&
      clientDataOfBirth
    ) {
      return (
        decedentState.decedentId_private.substring(7) === last4OnFile &&
        decedentState.dateOfBirth === clientDataOfBirth
      );
    }
    return false;
  },
);

export const selectDecedentAccounts = createSelector(
  selectDecedentInfoState,
  (decedentState: DecedentInfoState) => {
    return decedentState.decedentAccounts;
  },
);

export const selectDecedentAccountsTypes = createSelector(
  selectDecedentAccounts,
  (decedentAccounts) => {
    return new Set((decedentAccounts?.accounts ?? []).map((account) => account.accountType));
  },
);

// Toggle-gated: true if any transaction involves a managed decedent account.
// JOINT: checks selectedAccounts directly; IRA/INDV: falls back to accountTypeInheriting match.
export const selectIsDecedentAccountManaged = createSelector(
  selectDecedentAccounts,
  selectStateTransactions,
  selectInheritanceAdvisedClientWarningsToggle,
  (decedentAccounts, transactions, isInheritanceAdvisedClientWarningsToggle) => {
    if (!isInheritanceAdvisedClientWarningsToggle || !decedentAccounts?.accounts?.length) {
      return false;
    }
    return Object.values(transactions ?? {}).some((transaction) => {
      const selectedAccounts = transaction?.selectedClientAccounts?.transferFrom?.selectedAccounts;
      const accountType = transaction?.accountTriage?.accountTypeInheriting;
      if (accountType && JOINT_ACCOUNTS_TYPES.includes(accountType) && selectedAccounts?.length) {
        return selectedAccounts.some((selected) => selected.accountData?.managed);
      }
      if (!transaction?.accountTriage?.accountTypeInheriting) {
        return false;
      }
      return decedentAccounts.accounts.some(
        (account) =>
          (AANAccountTypeToAccountType[account.accountType] as string) ===
            (transaction.accountTriage.accountTypeInheriting as string) && account.managed,
      );
    });
  },
);

export const selectIsAdvisedClientModalShown = createSelector(
  selectDecedentInfoState,
  (state) => !!state.isAdvisedClientModalShown,
);
